import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase/client'
import { useAuth } from '@/features/auth/hooks/useAuth'
import type { Notification } from '../types'

export function useNotifications() {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Carregar todas as notificações
  useEffect(() => {
    if (!user) return

    const loadNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
        
        if (error) throw error
        
        setNotifications(Array.isArray(data) ? (data as Notification[]) : [])
      } catch (error) {
        console.error('Erro ao carregar notificações:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadNotifications()
  }, [user])

  // Inscrever-se em mudanças nas notificações (novas e updates)
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          setNotifications(prev => [...prev, payload.new as Notification])
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          setNotifications(prev => 
            prev.map(n => n.id === payload.new.id ? payload.new as Notification : n)
          )
        }
      )
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
  }, [user])

  // Marcar notificação como lida
  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase.rpc('mark_notification_as_read', {
        p_notification_id: id
      })

      if (error) throw error

      // Atualizar estado local
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n)
      )

      return { success: true }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error)
      return { success: false, error }
    }
  }

  // Marcar todas como lidas
  const markAllAsRead = async () => {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_as_read')

      if (error) throw error

      // Atualizar estado local
      setNotifications(prev => 
        prev.map(n => ({ ...n, read_at: new Date().toISOString() }))
      )

      return { success: true }
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error)
      return { success: false, error }
    }
  }

  return {
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead
  }
} 